import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import logo from '../assets/logo.png';
import defaultAvatar from '../assets/smart_mentor.png';

const HOME_SECTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'contact', label: 'Contact' },
];

const V2_SECTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'devices', label: 'Alerts' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'contact', label: 'Contact' },
];

const Navbar = () => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || null);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('accessToken'));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const dropdownRef = useRef();
  const mobileMenuRef = useRef();
  const closingMenuRef = useRef(false);

  const location = useLocation();
  const isV2 = location.pathname === '/v2';
  const sections = isV2 ? V2_SECTIONS : HOME_SECTIONS;
  const hashBase = isV2 ? '/v2' : '/';
  const homePath = isV2 ? '/v2' : '/';

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const toggleMobileMenu = () => {
    if (!closingMenuRef.current) {
      setMobileMenuOpen((prev) => !prev);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setIsLoggedIn(false);
    window.location.href = '/';
    setMobileMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isSectionActive = (sectionId) =>
    location.hash === `#${sectionId}` || (sectionId === 'home' && (location.hash === '' || location.hash === '#home'));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        if (!closingMenuRef.current) {
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <>
      <header className="sticky top-0 z-50">
        {/* Slim promo strip */}
        <div className="bg-[#6756FE] px-4 py-2 text-center text-[11px] font-semibold tracking-wide text-white sm:text-xs">
          Join 4,000+ traders · Live SMS alerts with entry, stop &amp; target
        </div>

        <nav
          className={`relative border-b transition-all duration-300 ${
            scrolled
              ? 'border-slate-200/80 bg-white/80 shadow-sm shadow-slate-200/40 backdrop-blur-xl'
              : 'border-transparent bg-white/95 backdrop-blur-md'
          }`}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
            {/* Brand */}
            <Link to={homePath} className="group flex min-w-0 items-center gap-2.5">
              <img src={logo} alt="It's All About The Options" className="h-9 w-9 object-contain sm:h-10 sm:w-10" />
              <span className="hidden truncate text-sm font-bold tracking-tight text-slate-900 transition group-hover:text-[#6756FE] sm:inline md:text-[15px]">
                It&apos;s All About The Options
              </span>
            </Link>

            {/* Desktop links */}
            <div className="hidden items-center gap-1 md:flex">
              {sections.map((section) => (
                <HashLink
                  key={section.id}
                  smooth
                  to={`${hashBase}#${section.id}`}
                  className={`rounded-full px-3.5 py-2 text-sm font-medium transition ${
                    isSectionActive(section.id)
                      ? 'bg-[#6756FE]/10 text-[#6756FE]'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {section.label}
                </HashLink>
              ))}
            </div>

            {/* Desktop auth */}
            <div className="hidden items-center gap-2 md:flex">
              {!isLoggedIn ? (
                <>
                  <Link
                    to="/signin"
                    className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/signup"
                    className="rounded-full bg-[#6756FE] px-5 py-2 text-sm font-semibold text-white shadow-md shadow-[#6756FE]/25 transition hover:bg-[#5646e8]"
                  >
                    Sign up
                  </Link>
                </>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pr-3 pl-1 transition hover:border-[#6756FE]/30 hover:bg-[#6756FE]/5"
                  >
                    <img
                      src={user?.dp || defaultAvatar}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover ring-2 ring-[#6756FE]/20"
                    />
                    <span className="max-w-[100px] truncate text-sm font-semibold text-slate-800">
                      {user?.first_name || 'User'}
                    </span>
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-200/60">
                      <Link
                        to="/profile"
                        className={`block px-4 py-2.5 text-sm transition ${
                          isActive('/profile')
                            ? 'bg-[#6756FE]/10 font-semibold text-[#6756FE]'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                        onClick={() => setDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => {
                if (mobileMenuOpen) {
                  closingMenuRef.current = true;
                  setMobileMenuOpen(false);
                  setTimeout(() => {
                    closingMenuRef.current = false;
                  }, 200);
                } else {
                  toggleMobileMenu();
                }
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-700 transition hover:border-[#6756FE]/30 hover:bg-[#6756FE]/5 md:hidden"
            >
              {mobileMenuOpen ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile panel */}
          {mobileMenuOpen && (
            <div
              ref={mobileMenuRef}
              className="border-t border-slate-100 bg-white/95 px-4 py-4 backdrop-blur-xl md:hidden"
            >
              <div className="flex flex-col gap-1">
                {sections.map((section) => (
                  <HashLink
                    key={section.id}
                    smooth
                    to={`${hashBase}#${section.id}`}
                    className={`rounded-xl px-4 py-3 text-sm font-medium transition ${
                      isSectionActive(section.id)
                        ? 'bg-[#6756FE]/10 text-[#6756FE]'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {section.label}
                  </HashLink>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4">
                {!isLoggedIn ? (
                  <>
                    <Link
                      to="/signin"
                      className="rounded-xl px-4 py-3 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Log in
                    </Link>
                    <Link
                      to="/signup"
                      className="rounded-xl bg-[#6756FE] px-4 py-3 text-center text-sm font-semibold text-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign up
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/profile"
                      className="rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Log out
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>
      <Outlet />
    </>
  );
};

export default Navbar;
